import React, { Suspense, lazy } from 'react';
import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';
import { ErrorPage } from 'src/shared/layout/ErrorPage';
import { FullScreenSpinner } from 'src/shared/layout/FullScreenSpinner';
import { AppWrapper } from './AppWrapper';
import { APP_ROUTES } from './routes';

const HomePage = lazy(() => import('src/pages/home').then(m => ({ default: m.HomePage })));
const FixturesPage = lazy(() => import('src/pages/fixtures').then(m => ({ default: m.FixturesPage })));
const ContactPage = lazy(() => import('src/pages/contact').then(m => ({ default: m.ContactPage })));
const DocumentsPage = lazy(() => import('src/pages/documents').then(m => ({ default: m.DocumentsPage })));
const CompetitionForm = lazy(() => import('src/pages/CompetitionForm'));
const TeamsPage = lazy(() => import('src/pages/teams'));
const StandingsPage = lazy(() => import('src/pages/standings'));
const AboutPage = lazy(() => import('src/pages/about'));
const ArticlesPage = lazy(() => import('src/pages/articles'));
const NewsListPage = lazy(() => import('src/pages/news/NewsListPage').then(m => ({ default: m.NewsListPage })));
const NewsDetailPage = lazy(() => import('src/pages/news/NewsDetailPage').then(m => ({ default: m.NewsDetailPage })));
const TeamPage = lazy(() => import('src/pages/teams/team').then(m => ({ default: m.TeamPage })));
const CompetitionsCalendarPage = lazy(() =>
  import('src/pages/competitionsCalendar').then(m => ({ default: m.CompetitionsCalendarPage })),
);
const ThankYouPage = lazy(() => import('src/pages/thankYou'));
const LoginPage = lazy(() => import('src/pages/login'));
const ForgotPasswordPage = lazy(() => import('src/pages/forgotPassword'));
const SetPasswordPage = lazy(() => import('src/pages/setPassword'));
const ParentPortalLayout = lazy(() => import('src/pages/parentPortal/ParentPortalLayout'));
const ParentBookingsPage = lazy(() => import('src/pages/parentPortal/bookings'));
const ParentCalendarPage = lazy(() => import('src/pages/parentPortal/calendar'));
const ParentChildrenPage = lazy(() => import('src/pages/parentPortal/children'));
const ParentMedicalDietaryPage = lazy(() => import('src/pages/parentPortal/medicalDietary'));
const ParentConsentsPage = lazy(() => import('src/pages/parentPortal/consents'));
const ParentIncidentsPage = lazy(() => import('src/pages/parentPortal/incidents'));
const ParentProfilePage = lazy(() => import('src/pages/parentPortal/profile'));
const ParentTermsPage = lazy(() => import('src/pages/parentPortal/terms'));
const ParentPrivacyPage = lazy(() => import('src/pages/parentPortal/privacy'));

const SuspenseOutlet: React.FC = () => (
  <Suspense fallback={<FullScreenSpinner />}>
    <Outlet />
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: APP_ROUTES.home,
    element: <AppWrapper />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <SuspenseOutlet />,
        children: [
          {
            index: true,
            element: <HomePage />,
          },
          {
            path: APP_ROUTES.teams.base,
            element: <Outlet />,
            children: [
              {
                index: true,
                element: <TeamsPage />,
              },
              {
                path: ':teamId',
                element: <TeamPage />,
              },
            ],
          },
          {
            path: APP_ROUTES.login,
            element: <LoginPage />,
          },
          {
            path: APP_ROUTES.forgotPassword,
            element: <ForgotPasswordPage />,
          },
          {
            path: '/forgotpassword',
            element: <ForgotPasswordPage />,
          },
          {
            path: APP_ROUTES.setPassword,
            element: <SetPasswordPage />,
          },
          {
            path: '/set-password',
            element: <SetPasswordPage />,
          },
          {
            path: '/setpassword',
            element: <SetPasswordPage />,
          },
          {
            path: APP_ROUTES.parentPortal.base,
            element: <ParentPortalLayout />,
            children: [
              {
                index: true,
                element: <Navigate replace to={APP_ROUTES.parentPortal.bookings} />,
              },
              {
                path: 'bookings',
                element: <ParentBookingsPage />,
              },
              {
                path: 'calendar',
                element: <ParentCalendarPage />,
              },
              {
                path: 'children',
                element: <ParentChildrenPage />,
              },
              {
                path: 'medical-dietary',
                element: <ParentMedicalDietaryPage />,
              },
              {
                path: 'consents',
                element: <ParentConsentsPage />,
              },
              {
                path: 'incidents-accidents',
                element: <ParentIncidentsPage />,
              },
              {
                path: 'profile',
                element: <ParentProfilePage />,
              },
              {
                path: 'terms-and-conditions',
                element: <ParentTermsPage />,
              },
              {
                path: 'privacy-and-communications',
                element: <ParentPrivacyPage />,
              },
            ],
          },
          {
            path: APP_ROUTES.about,
            element: <AboutPage />,
          },
          {
            path: APP_ROUTES.standings,
            element: <StandingsPage />,
          },
          {
            path: APP_ROUTES.calendar,
            element: <FixturesPage />,
          },
          {
            path: APP_ROUTES.eventsCalendar,
            element: <CompetitionsCalendarPage />,
          },
          {
            path: APP_ROUTES.contact,
            element: <ContactPage />,
          },
          {
            path: APP_ROUTES.documents,
            element: <DocumentsPage />,
          },
          {
            path: APP_ROUTES.articles,
            element: <ArticlesPage />,
          },
          {
            path: APP_ROUTES.news.base,
            element: <NewsListPage />,
          },
          {
            path: `${APP_ROUTES.news.base}/:slug`,
            element: <NewsDetailPage />,
          },
          {
            path: APP_ROUTES.competitions.base,
            element: <Outlet />,
            children: [
              {
                path: ':competitionId',
                element: <CompetitionForm />,
              },
            ],
          },
          {
            path: APP_ROUTES.thankyou,
            element: <ThankYouPage />,
          },
        ],
      },
    ],
  },
]);
